import type {
  Member,
  MemberResponse,
  MemberStreaks,
  MemberStreaksResponse,
  MemberCampaign,
  MemberCampaignResponse,
  ContentView,
  ContentViewResponse,
  Purchase,
  PurchaseResponse,
  Subscription,
  SubscriptionResponse,
  StreakSession,
  StreakSessionResponse,
  FixStreaksResult,
  FixStreaksResponse,
  CancelSubscriptionResult,
  CancelSubscriptionResponse,
  CancelGivingSubscriptionResult,
  CancelGivingSubscriptionResponse,
  RefundPurchaseResult,
  RefundPurchaseResponse,
  RefundGivingPaymentResult,
  RefundGivingPaymentResponse,
  Transaction,
  TransactionResponse,
  ProfileImageSignResult,
  ProfileImageSignResponse,
} from '../types';

export function mapMember(data: MemberResponse): Member {
  return {
    id: data.id,
    name: data.name,
    firstName: data.first_name,
    lastName: data.last_name,
    email: data.email,
    phone: data.phone,
    countryCode: data.country_code,
    profileImage: data.profile_image,
    bio: data.bio,
    type: data.type,
    city: data.city,
    state: data.state,
    country: data.country,
    zipcode: data.zipcode,
    gender: data.gender,
    birthday: data.birthday,
    handle: data.handle,
    sms: data.sms,
    marketingSms: data.marketing_sms,
    emailVerified: data.email_verified ?? null,
    isLeader: Boolean(data.is_leader),
    destroyed: data.destroyed,
    registeredAt: data.registered_at,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export function mapMemberStreaks(data: MemberStreaksResponse): MemberStreaks {
  return {
    currentStreak: data.current_streak,
    longestStreak: data.longest_streak,
    currentWeekStreak: data.current_week_streak,
    totalActiveDays: data.total_active_days,
    lastActiveDate: data.last_active_date,
    streakCountWithRepairs: data.streak_count_with_repairs,
    latestRepairableDays: data.latest_repairable_days,
  };
}

export function mapMemberCampaign(data: MemberCampaignResponse): MemberCampaign {
  return {
    id: data.id,
    status: data.status,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    campaign: {
      id: data.campaign.id,
      name: data.campaign.name,
      description: data.campaign.description,
      type: data.campaign.type,
    },
  };
}

export function mapContentView(data: ContentViewResponse): ContentView {
  return {
    id: data.id,
    completed: data.completed,
    totalCompletions: data.total_completions ?? 0,
    firstPlayedAt: data.created_at,
    lastPlayedAt: data.updated_at,
    content: {
      id: data.content.id,
      title: data.content.title,
      type: data.content.type,
      duration: data.content.duration,
    },
    artist: data.artist ? { id: data.artist.id, name: data.artist.name } : null,
  };
}

export function mapPurchase(data: PurchaseResponse): Purchase {
  return {
    id: data.id,
    store: data.store,
    transactionId: data.transaction_id,
    googleOrderId: data.google_order_id ?? null,
    appleTransactionId: data.apple_transaction_id ?? null,
    amount: data.amount,
    currency: data.currency,
    status: data.status,
    refunded: data.refunded,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    product: {
      id: data.product.id,
      name: data.product.name,
      type: data.product.type,
      sku: data.product.sku,
    },
  };
}

export function mapSubscription(data: SubscriptionResponse): Subscription {
  return {
    id: data.id,
    status: data.status,
    store: data.store,
    type: data.type,
    stripeSubscriptionId: data.stripe_subscription_id,
    stripeChargeId: data.stripe_charge_id,
    googleOrderId: data.google_order_id ?? null,
    appleTransactionId: data.apple_transaction_id ?? null,
    expiresAt: data.expires_at,
    startedAt: data.started_at,
    cancelledAt: data.cancelled_at,
    trialPeriodEndsAt: data.trial_period_ends_at ?? null,
    freeTrial: data.free_trial ?? null,
    billingInterval: data.billing_interval ?? null,
    associatedGivingSubscriptions: (data.associated_giving_subscriptions ?? []).map((giving) => ({
      id: giving.id,
      givingSubscriptionId: giving.giving_subscription_id,
      stripeAccountId: giving.stripe_account_id,
    })),
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    amount: data.amount,
    currency: data.currency,
    product: {
      id: data.product.id,
      name: data.product.name,
      type: data.product.type,
      sku: data.product.sku,
      subscriptionTier: data.product.subscription_tier,
    },
  };
}

export function mapStreakSession(data: StreakSessionResponse): StreakSession {
  return {
    streakDate: data.streak_date,
    isManualStreak: data.is_manual_streak,
  };
}

export function mapFixStreaksResult(data: FixStreaksResponse): FixStreaksResult {
  return {
    fixedCount: data.fixed_count,
    currentStreak: data.current_streak,
    failedDates: data.failed_dates,
  };
}

export function mapCancelSubscriptionResult(data: CancelSubscriptionResponse): CancelSubscriptionResult {
  return {
    success: data.success,
    subscriptionId: data.subscription_id,
  };
}

export function mapCancelGivingSubscriptionResult(
  data: CancelGivingSubscriptionResponse
): CancelGivingSubscriptionResult {
  return {
    success: data.success,
    givingSubscriptionId: data.giving_subscription_id,
  };
}

export function mapRefundPurchaseResult(data: RefundPurchaseResponse): RefundPurchaseResult {
  return {
    success: data.success,
    purchaseId: data.purchase_id,
  };
}

export function mapTransaction(data: TransactionResponse): Transaction {
  return {
    id: data.id,
    sourceType: data.source_type,
    purpose: data.purpose,
    accountScope: data.account_scope,
    store: data.store,
    stripeAccountId: data.stripe_account_id,
    chargeId: data.charge_id,
    subscriptionId: data.subscription_id,
    subscriptionRecordId: data.subscription_record_id,
    invoiceId: data.invoice_id,
    transactionId: data.transaction_id,
    googleOrderId: data.google_order_id,
    appleTransactionId: data.apple_transaction_id,
    amount: data.amount,
    currency: data.currency,
    amountRefunded: data.amount_refunded,
    refundableAmount: data.refundable_amount,
    status: data.status,
    refunded: data.refunded,
    refundStatus: data.refund_status,
    refundCreatedAt: data.refund_created_at,
    occurredAt: data.occurred_at,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    product: data.product
      ? {
          id: data.product.id,
          name: data.product.name,
          type: data.product.type,
          sku: data.product.sku,
        }
      : null,
    organization: data.organization ? { id: data.organization.id, name: data.organization.name } : null,
    fund: data.fund ? { id: data.fund.id, name: data.fund.name } : null,
    canRefund: data.can_refund,
    refundBlockedReason: data.refund_blocked_reason,
  };
}

export function mapRefundGivingPaymentResult(data: RefundGivingPaymentResponse): RefundGivingPaymentResult {
  return {
    success: data.success,
    givingPaymentId: data.giving_payment_id,
    refundId: data.refund_id,
    refundStatus: data.refund_status,
    amountRefunded: data.amount_refunded,
  };
}

export function mapProfileImageSignResult(data: ProfileImageSignResponse): ProfileImageSignResult {
  return {
    url: data.url,
    signedRequest: data.signed_request,
  };
}
